import { TINT } from '../shared/tints';

// Which tint a signal takes — the strip's chips and the slide-out's rows read the
// SAME function, so a signal never has two colours.
//
// Which tint a signal chip takes. `severity` (critical / warn / watch) is an
// urgency, so it cannot tell "waiting on them" from "needs you" — the chip's
// meaning has to come from the signal itself.
//
// Resolution order:
//   1. a `tint` the backend sends on the row (the knowledge base says to render
//      what comes back, not to hardcode signal kinds), when it is a known tint;
//   2. the explicit table below, keyed on the signal key;
//   3. the sentence's own direction — a stopgap for signal keys nobody has probed (see
//      SENTENCE_TINT): "Waiting on them for …" is the client's court, "… looks stalled" and
//      "They are waiting on a reply …" are ours;
//   4. neutral. An unmapped signal is calm gray, NEVER an alarm colour — a
//      missing mapping must not read as an emergency.
//
// The table lists only signal keys that appear in this codebase (now/kindActions,
// existing fixtures); the real set of `fn_signals_for` keys and categories has not
// been probed. It is a stopgap for a backend-supplied tint, tracked in the
// backend request doc.
export const SIGNAL_TINT = {
  // The client is waiting on us / something needs a person.
  unanswered_inbound: 'warn',
  request_unanswered: 'warn',
  needs_assistance: 'warn',
  agent_waiting: 'warn',
  payment_failed: 'warn',
  // We are waiting on the client.
  chase_client: 'waiting',
  // Informational.
  renewal_upcoming: 'neutral',
};

// Sentence shapes the server writes, read for WHOSE COURT the ball is in. Anchored and few on
// purpose: this is a stopgap until the backend sends a `tint` (or says which party is
// waiting), and a pattern that matched too much would colour things it should not. Order
// matters — the client's court is checked first.
export const SENTENCE_TINT = [
  // "Waiting on them for Update VLOG … — 22 days." — we are waiting on the client.
  [/^\s*waiting on (them|the client|client)\b/i, 'waiting'],
  // "tweak zap looks stalled — …", "… blocked", "… overdue" — it needs a person on our side.
  [/\b(stalled|blocked|overdue)\b/i, 'warn'],
  // "They are waiting on a reply about …" — the client is waiting on us.
  [/^\s*they are waiting on (a|your) (reply|response)\b/i, 'warn'],
  // "Newbook API credentials still not provided — 10th follow-up" — a chase that is not landing.
  [/\b(still (not|waiting|open|unanswered)|follow-up|unanswered|no (reply|response)|not (provided|received|answered))\b/i, 'warn'],
];

/**
 * @param {{signal?:string, tint?:string, says?:string, label?:string, severity?:string}} [sig]  One fn_signals_for row.
 * @returns {"warm"|"cool"|"positive"|"warn"|"waiting"|"neutral"}
 */
export const signalTint = (sig) => {
  if (sig?.tint && Object.prototype.hasOwnProperty.call(TINT, sig.tint)) return sig.tint;
  if (Object.prototype.hasOwnProperty.call(SIGNAL_TINT, sig?.signal)) return SIGNAL_TINT[sig.signal];
  const text = sig?.says || sig?.label;
  if (typeof text === 'string') {
    for (const [pattern, tint] of SENTENCE_TINT) if (pattern.test(text)) return tint;
  }
  return 'neutral';
};
